import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { usePlayer } from '../../context/PlayerContext';
import { toast } from 'sonner';
import BeatUploadForm from '../../components/BeatUploadForm';
import { API } from '../../config/api';
import {
  Music, Settings, Users, Eye, Upload, Play, Pause,
  Loader2, Trash2, Edit3, Headphones, Heart, BarChart3,
  Disc3, Clock, Plus, TrendingUp, Share2, MessageSquare
} from 'lucide-react';

function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function BeatRow({ beat, onDelete, onPlay, isPlaying }) {
  return (
    <div
      data-testid={`beat-row-${beat.beat_id}`}
      className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border hover:border-orange-500/20 transition-all group"
    >
      {/* Cover / Play */}
      <button
        onClick={() => onPlay(beat)}
        className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 relative"
        data-testid={`play-beat-${beat.beat_id}`}
      >
        {beat.cover_art_url ? (
          <img src={beat.cover_art_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-600/20 to-amber-600/10 flex items-center justify-center">
            <Disc3 className="w-5 h-5 text-orange-400/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
        </div>
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{beat.title}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {beat.genre && <span>{beat.genre}</span>}
          {beat.bpm && <span>{beat.bpm} BPM</span>}
          {beat.key && <span>{beat.key}</span>}
          {beat.duration > 0 && <span>{formatDuration(beat.duration)}</span>}
        </div>
      </div>

      {/* Stats */}
      <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Headphones className="w-3 h-3" />{beat.plays_count || 0}</span>
        <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{beat.likes_count || 0}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost" size="icon"
          className="w-8 h-8 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => onDelete(beat.beat_id)}
          data-testid={`delete-beat-${beat.beat_id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function ProducerDashboard() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const { playTrack, currentTrack, isPlaying } = usePlayer();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [beats, setBeats] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('beats');
  const [showUpload, setShowUpload] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    if (user?.role && user.role !== 'producer' && user.role !== 'owner') {
      navigate(`/dashboard/${user.role}`);
      return;
    }
    fetchDashboard();
  }, [isAuthenticated, authLoading, user]);

  const fetchDashboard = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/producers/me/dashboard`, { headers });
      if (res.ok) {
        const data = await res.json();
        setBeats(data.beats || []);
        setStats(data.stats || {});
      }
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/producers/me/analytics`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch {}
  };

  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) fetchAnalytics();
  }, [activeTab]);

  const handlePlayBeat = (beat) => {
    // Map beat to track format for global player
    playTrack({
      track_id: beat.beat_id,
      title: beat.title,
      artist_name: beat.producer_name || user?.name || 'Producer',
      cover_url: beat.cover_art_url,
      audio_url: beat.audio_url,
      duration: beat.duration
    });
    // Record play
    fetch(`${API}/beats/${beat.beat_id}/play`, { method: 'POST' }).catch(() => {});
  };

  const handleDeleteBeat = async (beatId) => {
    if (!confirm('Delete this beat?')) return;
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/beats/${beatId}`, { method: 'DELETE', headers });
      if (res.ok) {
        setBeats(beats.filter(b => b.beat_id !== beatId));
        setStats(s => ({ ...s, total_beats: (s.total_beats || 1) - 1 }));
        toast.success('Beat deleted');
      }
    } catch { toast.error('Failed to delete'); }
  };

  const handleUploadSuccess = (newBeat) => {
    setBeats([newBeat, ...beats]);
    setStats(s => ({ ...s, total_beats: (s.total_beats || 0) + 1 }));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  const TABS = [
    { key: 'beats', label: 'Beats', icon: Disc3 },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen pb-32" data-testid="producer-dashboard">
      {/* Header */}
      <div className="border-b border-border bg-background/50">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-border" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-600 to-amber-700 flex items-center justify-center">
                  <Music className="w-7 h-7 text-foreground/80" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white">{user?.name || 'Producer'}</h1>
                <p className="text-sm text-muted-foreground">@{user?.handle || 'handle'}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => setShowUpload(true)}
                data-testid="upload-beat-btn"
              >
                <Upload className="w-4 h-4 mr-2" />Upload Beat
              </Button>
              <Button variant="outline" className="border-border" onClick={() => navigate(`/producers/${user?.handle}`)}>
                <Eye className="w-4 h-4 mr-2" />Profile
              </Button>
              <Button variant="outline" className="border-border" onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
            {[
              { label: 'Beats', value: stats.total_beats || 0, icon: Disc3 },
              { label: 'Plays', value: stats.total_plays || 0, icon: Headphones },
              { label: 'Likes', value: stats.total_likes || 0, icon: Heart },
              { label: 'Followers', value: stats.followers_count || 0, icon: Users },
              { label: 'Views', value: stats.profile_views || 0, icon: Eye },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/20 border border-border">
                <s.icon className="w-4 h-4 text-orange-400/70" />
                <div>
                  <p className="text-lg font-bold text-foreground leading-none">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 mt-6">
        <div className="flex gap-1 mb-5">
          {TABS.map(tab => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'ghost'}
              className={activeTab === tab.key ? 'bg-orange-600 hover:bg-orange-700' : 'text-muted-foreground'}
              onClick={() => setActiveTab(tab.key)}
              size="sm"
            >
              <tab.icon className="w-3.5 h-3.5 mr-1.5" />{tab.label}
            </Button>
          ))}
        </div>

        {/* Beats Tab */}
        {activeTab === 'beats' && (
          <div>
            {beats.length === 0 ? (
              <Card className="bg-muted/20 border-border p-12 text-center">
                <Disc3 className="w-14 h-14 text-muted-foreground/80 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground/80 mb-2">No beats yet</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                  Upload your first beat to start building your catalog and getting discovered
                </p>
                <Button
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setShowUpload(true)}
                  data-testid="upload-first-beat-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />Upload Your First Beat
                </Button>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="beats-list">
                {beats.map(beat => (
                  <BeatRow
                    key={beat.beat_id}
                    beat={beat}
                    onDelete={handleDeleteBeat}
                    onPlay={handlePlayBeat}
                    isPlaying={isPlaying && currentTrack?.track_id === beat.beat_id}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {!analytics ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
            ) : (
              <div className="space-y-6">
                {/* Overview Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Total Plays', value: analytics.total_plays, icon: Headphones, color: 'text-orange-400' },
                    { label: 'Total Likes', value: analytics.total_likes, icon: Heart, color: 'text-pink-400' },
                    { label: 'Followers', value: analytics.followers_count, icon: Users, color: 'text-blue-400' },
                    { label: 'Profile Views', value: analytics.profile_views, icon: Eye, color: 'text-green-400' },
                    { label: 'Total Beats', value: analytics.total_beats, icon: Disc3, color: 'text-amber-400' },
                    { label: 'Following', value: analytics.following_count, icon: TrendingUp, color: 'text-purple-400' },
                  ].map(s => (
                    <Card key={s.label} className="bg-muted/20 border-border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <s.icon className={`w-4 h-4 ${s.color}`} />
                        <span className="text-xs text-muted-foreground">{s.label}</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{s.value || 0}</p>
                    </Card>
                  ))}
                </div>

                {/* Top Beats */}
                {analytics.top_beats?.length > 0 && (
                  <Card className="bg-muted/20 border-border p-5">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-orange-400" />Top Performing Beats
                    </h3>
                    <div className="space-y-3">
                      {analytics.top_beats.map((beat, i) => (
                        <div key={beat.beat_id} className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground/80 w-5 text-right">#{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{beat.title}</p>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Headphones className="w-3 h-3" />{beat.plays_count || 0}</span>
                            <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{beat.likes_count || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <BeatUploadForm
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
}
